import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/I18nContext';
import { Button } from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiCheckCircle } = FiIcons;

export const ForSuppliers = () => {
  const { t } = useTranslation();

  const steps = ['step1', 'step2', 'step3', 'step4', 'step5'];

  return (
    <div className="min-h-screen bg-[#FCFAF8] pt-48 pb-40">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Header Section */}
          <header className="mb-32 border-b border-zinc-100 pb-20">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-6xl font-medium text-zinc-900 tracking-[-0.02em] mb-10 leading-[1.1]"
            >
              {t('forSuppliers.title')}
            </motion.h1>
            <p className="text-xl md:text-2xl text-zinc-400 font-light max-w-2xl">
              {t('forSuppliers.subtitle')}
            </p>
          </header>

          {/* Discovery Section */}
          <section className="mb-32">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <h2 className="text-3xl font-semibold text-zinc-900 tracking-tight">
                  {t('forSuppliers.whyJoin.title')}
                </h2>
                <p className="text-zinc-500 leading-[1.85] text-lg font-light">
                  {t('forSuppliers.whyJoin.content')}
                </p>
              </div>
              <div className="aspect-square rounded-[2rem] overflow-hidden bg-zinc-100 shadow-xl">
                <img 
                  src="https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=800" 
                  className="w-full h-full object-cover" 
                  alt="Supplier at work"
                />
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="mb-48 bg-white rounded-[3rem] p-12 md:p-20 border border-zinc-100 shadow-sm">
            <h2 className="text-3xl font-semibold text-zinc-900 tracking-tight mb-12">
              {t('forSuppliers.benefits.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              {t('forSuppliers.benefits.content').split('\n').map((benefit, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-[#D4B483] mt-1 shrink-0" />
                  <p className="text-zinc-600 font-medium leading-relaxed">
                    {benefit.replace('• ', '').trim()}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Onboarding Steps */}
          <section className="mb-48">
            <h2 className="text-3xl font-semibold text-zinc-900 tracking-tight mb-20 text-center">
              {t('forSuppliers.onboarding.title')}
            </h2>
            <div className="space-y-24">
              {steps.map((stepKey, idx) => (
                <div key={stepKey} className="relative flex flex-col md:flex-row gap-8 md:gap-16">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#1A2E22] text-[#D4B483] rounded-2xl flex items-center justify-center text-xl font-black shadow-lg">
                    {idx + 1}
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-bold text-zinc-900 mb-4 uppercase tracking-wider">
                      {t(`forSuppliers.onboarding.${stepKey}.title`)}
                    </h3>
                    <p className="text-zinc-500 text-lg font-light leading-relaxed">
                      {t(`forSuppliers.onboarding.${stepKey}.content`)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="text-center pt-24 border-t border-zinc-100">
            <h2 className="text-4xl font-bold text-zinc-900 mb-10 tracking-tight">
              {t('forSuppliers.cta.title')}
            </h2>
            <Link to="/login">
              <Button size="lg" className="h-16 px-16 rounded-full text-lg gap-3">
                {t('forSuppliers.cta.button')}
                <SafeIcon icon={FiArrowRight} />
              </Button>
            </Link>
          </section>
        </motion.div>
      </div>
    </div>
  );
};